import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useAtomValue, useSetAtom } from 'jotai';
import { scheduleEntriesState, supervisorAssignmentDaysState, tasksState, contractorsState } from '../jotai/atoms';
import SupervisorAssignmentDetailModal from './SupervisorAssignmentDetailModal';
import TaskDetailModal from './TaskDetailModal'; // TaskDetailModal 임포트
import AssociationReportDetailModal from './AssociationReportDetailModal'; // New modal import
import '../App.css';

function HomePage() {
  const scheduleEntries = useAtomValue(scheduleEntriesState);
  const supervisorAssignmentDays = useAtomValue(supervisorAssignmentDaysState);
  const tasks = useAtomValue(tasksState); // tasksState 추가
  const contractors = useAtomValue(contractorsState);
  const setTasks = useSetAtom(tasksState); // setTasks 추가
  const setSupervisorAssignmentDays = useSetAtom(supervisorAssignmentDaysState);

  const [activeTab, setActiveTab] = useState('today'); // 'today', 'week', or 'replacement'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [assignmentDetails, setAssignmentDetails] = useState([]);
  const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = useState(false); // TaskDetailModal 상태
  const [selectedTask, setSelectedTask] = useState(null); // 선택된 Task 정보
  const [isReportDetailModalOpen, setIsReportDetailModalOpen] = useState(false); // 신고관리 모달 상태
  const [selectedTaskForReport, setSelectedTaskForReport] = useState(null); // 신고관리 대상 공사
  const [isModalReadOnly, setIsModalReadOnly] = useState(false); // 모달 읽기 전용 상태
  const [currentMonth, setCurrentMonth] = useState(new Date()); // 현재 표시할 월
  const [selectedYear, setSelectedYear] = useState('all'); // 선택된 연도 상태 추가, 기본값 'all'

  const fetchAllTasks = useCallback(async () => {
    try {
      const tasksResponse = await axiosInstance.get('/tasks');
      const fetchedTasks = tasksResponse.data;

      const tasksWithSupervisorChanges = await Promise.all(fetchedTasks.map(async task => {
        let supervisorChangedDate = null;
        if (task.id) {
          const supervisorChangesResponse = await axiosInstance.get(`/association-reports/supervisor-changes/${task.id}`);
          supervisorChangedDate = supervisorChangesResponse.data;
        }
        return {
          ...task,
          supervisorChangedDate: supervisorChangedDate,
        };
      }));
      setTasks(tasksWithSupervisorChanges);
    } catch (error) {
      console.error('Error fetching tasks with supervisor changes:', error);
    }
  }, [setTasks]);

  useEffect(() => {
    fetchAllTasks();
  }, [fetchAllTasks]);

  const currentYear = new Date().getFullYear();
  const years = ['all', ...Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString())]; // 현재 연도 기준 앞뒤 5년 + 전체 옵션

  const [unitPriceTotal, setUnitPriceTotal] = useState(0);
  const [unitPriceCompletionTotal, setUnitPriceCompletionTotal] = useState(0); // 단가 준공 금액
  const [unitPriceRemainingTotal, setUnitPriceRemainingTotal] = useState(0); // 단가 잔액

  const [lumpSumTotal, setLumpSumTotal] = useState(0);
  const [lumpSumCompletionTotal, setLumpSumCompletionTotal] = useState(0); // 총가 준공 금액
  const [lumpSumRemainingTotal, setLumpSumRemainingTotal] = useState(0); // 총가 잔액

  const [totalContractAmount, setTotalContractAmount] = useState(0); // 전체 계약 금액 추가
  const [totalCompletionAmount, setTotalCompletionAmount] = useState(0); // 총 준공 금액 추가
  const [totalRemainingAmount, setTotalRemainingAmount] = useState(0); // 총 잔액 추가
  const [contractAmountTab, setContractAmountTab] = useState('unitPrice'); // 계약 금액 현황 탭 상태 추가
  const [workOrdersByStatus, setWorkOrdersByStatus] = useState({
    'todo': { name: '할 일', items: [] },
    'in-progress': { name: '진행 중', items: [] },
    'review': { name: '검토 중', items: [] },
    'done': { name: '완료', items: [] },
  });
  const [workOrderTab, setWorkOrderTab] = useState('todo'); // 작업 지시 탭 상태 추가

  useEffect(() => {
    // contractAmountTab이 변경될 때 selectedYear 재설정
    if (contractAmountTab === 'unitPrice') {
      setSelectedYear(new Date().getFullYear().toString());
    } else if (contractAmountTab === 'lumpSum' || contractAmountTab === 'all') {
      setSelectedYear('all');
    }
  }, [contractAmountTab]); // contractAmountTab이 변경될 때만 실행

  useEffect(() => {
    const fetchWorkOrders = async () => {
      try {
        const response = await axiosInstance.get('/work-orders');
        const fetchedWorkOrders = response.data;

        const newWorkOrdersByStatus = {
          'todo': { name: '할 일', items: [] },
          'in-progress': { name: '진행 중', items: [] },
          'review': { name: '검토 중', items: [] },
          'done': { name: '완료', items: [] },
        };

        fetchedWorkOrders.forEach(order => {
          if (newWorkOrdersByStatus[order.status]) {
            if (order.status === 'done') {
              if (order.completedAt) {
                const completedDate = new Date(order.completedAt);
                const today = new Date();
                if (
                  completedDate.getFullYear() === today.getFullYear() &&
                  completedDate.getMonth() === today.getMonth() &&
                  completedDate.getDate() === today.getDate()
                ) {
                  newWorkOrdersByStatus[order.status].items.push(order);
                }
              }
            } else {
              newWorkOrdersByStatus[order.status].items.push(order);
            }
          }
        });
        setWorkOrdersByStatus(newWorkOrdersByStatus);
      } catch (error) {
        console.error('Error fetching work orders:', error);
      }
    };
    fetchWorkOrders();
  }, []);
  
  useEffect(() => {
    let unitTotal = 0;
    let unitCompletionTotal = 0;

    let lumpTotal = 0;
    let lumpCompletionTotal = 0;

    // 선택된 연도에 따라 tasks 필터링
    const filteredTasks = selectedYear === 'all'
      ? tasks
      : tasks.filter(task => {
          const taskYear = new Date(task.contractDate).getFullYear().toString();
          return taskYear === selectedYear;
        });

    filteredTasks.forEach(task => { // 필터링된 tasks 사용
      const initialContract = parseFloat(String(task.initialContractAmount).replace(/,/g, '')) || 0;
      const change = parseFloat(String(task.changeAmount).replace(/,/g, '')) || 0;
      const contractAmount = initialContract + change;
      const completionAmount = parseFloat(String(task.completionAmount).replace(/,/g, '')) || 0;

      if (task.type === '단가') {
        unitTotal += contractAmount;
        unitCompletionTotal += completionAmount;
      } else if (task.type === '총가') {
        lumpTotal += contractAmount;
        lumpCompletionTotal += completionAmount;
      }
    });

    const overallContractTotal = unitTotal + lumpTotal;
    const overallCompletionTotal = unitCompletionTotal + lumpCompletionTotal;
    const overallRemainingTotal = overallContractTotal - overallCompletionTotal;

    setUnitPriceTotal(unitTotal);
    setUnitPriceCompletionTotal(unitCompletionTotal);
    setUnitPriceRemainingTotal(unitTotal - unitCompletionTotal);

    setLumpSumTotal(lumpTotal);
    setLumpSumCompletionTotal(lumpCompletionTotal);
    setLumpSumRemainingTotal(lumpTotal - lumpCompletionTotal);

    setTotalContractAmount(overallContractTotal);
    setTotalCompletionAmount(overallCompletionTotal);
    setTotalRemainingAmount(overallRemainingTotal);
  }, [tasks, selectedYear]);

  const handleSupervisorClick = useCallback(async (name) => {
    setSelectedSupervisor(name);
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    try {
      // name 변수를 encodeURIComponent()로 감싸줍니다.
      const encodedName = encodeURIComponent(name);
      const response = await axiosInstance.get(`/schedule-entries/details/${year}/${month}/${encodedName}`);
      
      setAssignmentDetails(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching supervisor details:", error);
    }
  }, [currentMonth]);

  const fetchSupervisorAssignmentDays = useCallback(async (year, month) => {
    try {
      const response = await axiosInstance.get(`/schedule-entries/assignment-days/${year}/${month}`);
      setSupervisorAssignmentDays(response.data);
    } catch (error) {
      console.error('Error fetching supervisor assignment days:', error);
    }
  }, [setSupervisorAssignmentDays]);

  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    fetchSupervisorAssignmentDays(year, month);
  }, [currentMonth, fetchSupervisorAssignmentDays]);

  const handlePrevMonth = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() - 1);
      return newMonth;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + 1);
      return newMonth;
    });
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sevenDaysLater = new Date();
  sevenDaysLater.setDate(today.getDate() + 6); // Today + 6 days = 7 days total
  sevenDaysLater.setHours(23, 59, 59, 999);

  const todaySchedule = scheduleEntries.filter(entry => {
    const entryDate = new Date(entry.scheduledDate);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate.getTime() === today.getTime();
  });

  const weekSchedule = scheduleEntries.filter(entry => {
    const entryDate = new Date(entry.scheduledDate);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate.getTime() >= today.getTime() && entryDate.getTime() <= sevenDaysLater.getTime();
  });

  const todayFormatted = today.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  const sevenDaysLaterFormatted = sevenDaysLater.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  const replacementTasks = tasks.filter(task => !!task.supervisorChangedDate).sort((a, b) => {
    const dateA = new Date(new Date(a.supervisorChangedDate).setDate(new Date(a.supervisorChangedDate).getDate() + 29));
    const dateB = new Date(new Date(b.supervisorChangedDate).setDate(new Date(b.supervisorChangedDate).getDate() + 29));
    return dateA.getTime() - dateB.getTime();
  });

  // 인라인 테이블 스타일 정의
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '15px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  };

  const thStyle = {
    padding: '12px 15px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#f8f8f8',
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center'
  };

  const tdStyle = {
    padding: '12px 15px',
    border: '1px solid #e0e0e0',
    color: '#555'
  };

  const tdNumberStyle = {
    ...tdStyle,
    textAlign: 'right',
    fontWeight: 'bold',
    color: '#007bff'
  };

  const totalRowStyle = {
    backgroundColor: '#e6f7ff'
  };

  const totalTdStyle = {
    ...tdStyle,
    backgroundColor: '#e6f7ff',
    fontWeight: 'bold',
    color: '#0056b3'
  };

  const totalTdNumberStyle = {
    ...tdNumberStyle,
    backgroundColor: '#e6f7ff',
    color: '#0056b3'
  };

  return (
    <div className="container home-page-layout">
      <div className="quadrant top-left">
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => setActiveTab('today')}
          >
            오늘 일정
          </button>
          <button
            className={`tab-button ${activeTab === 'week' ? 'active' : ''}`}
            onClick={() => setActiveTab('week')}
          >
            7일간 일정
          </button>
          <button
            className={`tab-button ${activeTab === 'replacement' ? 'active' : ''}`}
            onClick={() => setActiveTab('replacement')}
          >
            협회신고(교체)
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'today' && (
            <>
              <h3>{todayFormatted} 공사 일정</h3>
              {todaySchedule.length > 0 ? (
                <ul className="schedule-list">
                  {todaySchedule.map(entry => {
                    if (!entry.task) return null; // Skip rendering if task is missing
                    const taskName = entry.task.name || '';
                    const truncatedName = taskName.length > 20 ? `${taskName.substring(0, 20)}...` : taskName;
                    const scheduledDate = new Date(entry.scheduledDate).toLocaleDateString('ko-KR');

                    return (
                      <li key={entry.id} onClick={() => {
                        const fullTask = tasks.find(t => t.id === entry.task.id);
                        setSelectedTask(fullTask || entry.task);
                        setIsTaskDetailModalOpen(true);
                      }} style={{ cursor: 'pointer' }}>
                        <div style={{ textAlign: 'left' }}>
                          <div>배치날짜: {scheduledDate}</div>
                          <div>번호: {entry.task.number}</div>
                          <div>공사명: {truncatedName}</div>
                          <div>책임: {entry.assignedManagers} / 보조: {entry.assignedAssistants || '없음'}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>오늘 등록된 공사 일정이 없습니다.</p>
              )}
            </>
          )}

          {activeTab === 'week' && (
            <> 
              <h3>{todayFormatted} ~ {sevenDaysLaterFormatted} 공사 일정</h3>
              {weekSchedule.length > 0 ? (
                <ul className="schedule-list">
                  {weekSchedule.map(entry => {
                    if (!entry.task) return null; // Skip rendering if task is missing
                    const taskName = entry.task.name || '';
                    const truncatedName = taskName.length > 20 ? `${taskName.substring(0, 20)}...` : taskName;
                    const scheduledDate = new Date(entry.scheduledDate).toLocaleDateString('ko-KR');

                    return (
                      <li key={entry.id} onClick={() => {
                        const fullTask = tasks.find(t => t.id === entry.task.id);
                        setSelectedTask(fullTask || entry.task);
                        setIsTaskDetailModalOpen(true);
                      }} style={{ cursor: 'pointer' }}>
                        <div style={{ textAlign: 'left' }}>
                          <div>배치날짜: {scheduledDate}</div>
                          <div>번호: {entry.task.number}</div>
                          <div>공사명: {truncatedName}</div>
                          <div>책임: {entry.assignedManagers} / 보조: {entry.assignedAssistants || '없음'}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>오늘 포함 7일간 등록된 공사 일정이 없습니다.</p>
              )}
            </>
          )}

          {activeTab === 'replacement' && (
            <>
              <h3>협회신고(교체) 공사 목록</h3>
              {replacementTasks.length > 0 ? (
                <ul className="schedule-list">
                  {replacementTasks.map(task => {
                    const taskName = task.name || '';
                    const truncatedName = taskName.length > 20 ? `${taskName.substring(0, 20)}...` : taskName;
                    const supervisorChangedDate = task.supervisorChangedDate ? new Date(new Date(task.supervisorChangedDate).setDate(new Date(task.supervisorChangedDate).getDate() + 29)).toLocaleDateString('ko-KR') : 'N/A';

                    return (
                      <li key={task.id} onClick={() => {
                        setSelectedTaskForReport(task);
                        setIsReportDetailModalOpen(true);
                        setIsModalReadOnly(true); // 읽기 전용 모드
                      }} style={{ cursor: 'pointer' }}>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ color: 'red' }}>신고기한: {supervisorChangedDate}까지</div>
                          <div>번호: {task.number}</div>
                          <div>공사명: {truncatedName}</div>
                          <div>책임: {task.managers} / 보조: {task.assistants || '없음'}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>교체된 협회신고 공사가 없습니다.</p>
              )}
            </>
          )}
        </div>
      </div>
      
      <div className="quadrant top-right">
        <div className="month-navigation">
          <button onClick={handlePrevMonth}>&lt;</button>
          <h3>{currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월 감리원 배치 일수</h3>
          <button onClick={handleNextMonth}>&gt;</button>
        </div>
        {Object.keys(supervisorAssignmentDays).length > 0 ? (
          <ul className="schedule-list">
            {Object.entries(supervisorAssignmentDays).map(([name, days]) => (
              <li key={name} onClick={() => handleSupervisorClick(name)} style={{ cursor: 'pointer' }}>
                <strong>{name}</strong>: {days}일
              </li>
            ))}
          </ul>
        ) : (
          <p>선택된 달에 감리원 배치 기록이 없습니다.</p>
        )}
      </div>
      
      <div className="quadrant bottom-left">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <h3 style={{ margin: 0 }}>계약 금액 현황</h3>
          <div className="year-select-container" style={{ position: 'absolute', right: 0 }}>
            <select onChange={(e) => setSelectedYear(e.target.value)} value={selectedYear} className="year-select">
              <option value="all">전체</option>
              {years.filter(year => year !== 'all').map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
          </div>
        </div>
        <div className="tabs">
          <button
            className={`tab-button ${contractAmountTab === 'unitPrice' ? 'active' : ''}`}
            onClick={() => setContractAmountTab('unitPrice')}
          >
            단가
          </button>
          <button
            className={`tab-button ${contractAmountTab === 'lumpSum' ? 'active' : ''}`}
            onClick={() => setContractAmountTab('lumpSum')}
          >
            총가
          </button>
          <button
            className={`tab-button ${contractAmountTab === 'all' ? 'active' : ''}`}
            onClick={() => setContractAmountTab('all')}
          >
            전체
          </button>
        </div>
        <div className="tab-content">
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>구분</th>
                <th style={thStyle}>총 계약 금액</th>
                <th style={thStyle}>준공 금액(기성)</th>
                <th style={thStyle}>잔액</th>
              </tr>
            </thead>
            <tbody>
              {/* 단가 행 */}
              {contractAmountTab === 'unitPrice' || contractAmountTab === 'all' ? (
                <tr>
                  <td style={tdStyle}>단가</td>
                  <td style={tdNumberStyle}>{unitPriceTotal.toLocaleString()}원</td>
                  <td style={tdNumberStyle}>{unitPriceCompletionTotal.toLocaleString()}원</td>
                  <td style={tdNumberStyle}>{unitPriceRemainingTotal.toLocaleString()}원</td>
                </tr>
              ) : null}

              {/* 총가 행 */}
              {contractAmountTab === 'lumpSum' || contractAmountTab === 'all' ? (
                <tr>
                  <td style={tdStyle}>총가</td>
                  <td style={tdNumberStyle}>{lumpSumTotal.toLocaleString()}원</td>
                  <td style={tdNumberStyle}>{lumpSumCompletionTotal.toLocaleString()}원</td>
                  <td style={tdNumberStyle}>{lumpSumRemainingTotal.toLocaleString()}원</td>
                </tr>
              ) : null}

              {/* 전체 행 (all 탭에서만 보이도록) */}
              {contractAmountTab === 'all' ? (
                <tr style={totalRowStyle}>
                  <td style={totalTdStyle}>전체</td>
                  <td style={totalTdNumberStyle}>{totalContractAmount.toLocaleString()}원</td>
                  <td style={totalTdNumberStyle}>{totalCompletionAmount.toLocaleString()}원</td>
                  <td style={totalTdNumberStyle}>{totalRemainingAmount.toLocaleString()}원</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="quadrant bottom-right">
        <h3>작업 지시 현황</h3>
        <div className="tabs">
          {Object.entries(workOrdersByStatus).map(([statusKey, statusData]) => (
            <button
              key={statusKey}
              className={`tab-button ${workOrderTab === statusKey ? 'active' : ''}`}
              onClick={() => setWorkOrderTab(statusKey)}
            >
              {statusData.name} ({statusData.items.length})
            </button>
          ))}
        </div>
        <div className="tab-content">
          <div className="work-order-list">
            {workOrdersByStatus[workOrderTab].items.length > 0 ? (
              <ul>
                {workOrdersByStatus[workOrderTab].items.map(order => {
                  let content = <span>{order.title}</span>;

                  if (order.type === '기타') {
                    let detailsContent = '';
                    let allTitle ='';
                    if (order.details && typeof order.details === 'string') {
                      try {
                        const parsedDetails = JSON.parse(order.details);
                        detailsContent = parsedDetails.otherDetails || '';
                        allTitle = parsedDetails.otherTitle || '';
                      } catch (e) {
                        console.error('Error parsing work order details:', e);
                      }
                    }
                    const truncatedDetails = detailsContent.length > 20 ? `${detailsContent.substring(0, 20)}...` : detailsContent;
                    const title = allTitle.length > 20 ? `${allTitle.substring(0, 20)}...` : allTitle;
                    content = (
                      <div style={{ textAlign: 'left' }}>
                        <div>종류: {order.type}</div>
                        <div>제목: {title}</div>
                        <div>내용: {truncatedDetails}</div>
                      </div>
                    );
                  } else if (order.type === '시공통보') {
                    if (order.details && typeof order.details === 'string') {
                      try {
                        const parsedDetails = JSON.parse(order.details);
                        const noticeDetails = parsedDetails.noticeDetails || '';
                        const truncatedNoticeDetails = noticeDetails.length > 10 ? `${noticeDetails.substring(0, 10)}...` : noticeDetails;
                        content = (
                          <div style={{ textAlign: 'left' }}>
                            <div>종류: {order.type}</div>
                            <div>공사명: {truncatedNoticeDetails}</div>
                            <div>책임: {parsedDetails.principalSupervisor || '없음'} / 보조: {parsedDetails.assistantSupervisor || '없음'}</div>
                          </div>
                        );
                      } catch (e) {
                        console.error('Error parsing work order details:', e);
                        content = <span>{order.title}</span>;
                      }
                    } else {
                      content = <span>{order.title}</span>;
                    }
                  } else if (order.type === '배치') {
                    if (order.details && typeof order.details === 'string') {
                      try {
                        const parsedDetails = JSON.parse(order.details);
                        const assignment = parsedDetails.assignments && parsedDetails.assignments[0] ? parsedDetails.assignments[0] : {};
                        const taskName = assignment.taskName || '';
                        const truncatedName = taskName.length > 20 ? `${taskName.substring(0, 20)}...` : taskName;
                        content = (
                          <div style={{ textAlign: 'left' }}>
                            <div>종류: {order.type}</div>
                            <div>번호: {parsedDetails.taskNumber || ''}</div>
                            <div>공사명: {truncatedName}</div>
                            <div>책임: {assignment.assignedManagers || '없음'} / 보조: {assignment.assignedAssistants || '없음'}</div>
                          </div>
                        );
                      } catch (e) {
                        console.error('Error parsing work order details:', e);
                        content = <span>{order.title}</span>;
                      }
                    } else {
                      content = <span>{order.title}</span>;
                    }
                  }

                  return (
                    <li key={order.id}>
                      {content}
                      <span className={`status-badge status-${order.status}`}>{order.status}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p>해당하는 작업 지시가 없습니다.</p>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <SupervisorAssignmentDetailModal 
          supervisorName={selectedSupervisor}
          details={assignmentDetails}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {isTaskDetailModalOpen && selectedTask && (
        <TaskDetailModal
          isOpen={isTaskDetailModalOpen}
          onClose={() => setIsTaskDetailModalOpen(false)}
          task={selectedTask}
          onUpdateTask={() => {}}
          onDeleteTask={() => {}}
          supervisors={[]}
          contractors={contractors}
          isEditMode={false}
        />
      )}

      {selectedTaskForReport && (
        <AssociationReportDetailModal
          isOpen={isReportDetailModalOpen}
          onClose={() => setIsReportDetailModalOpen(false)}
          task={selectedTaskForReport}
          onSaveReportDate={() => { /* HomePage에서는 저장 기능이 필요 없으므로 빈 함수로 둡니다. */ }}
          onReportChange={() => { /* HomePage에서는 리스트 갱신이 필요할 수 있으나, 현재는 빈 함수로 둡니다. */ }}
          isReadOnly={isModalReadOnly}
        />
      )}
      
      <div style={{ textAlign: 'center', width: '100%', color: '#777', padding: '0px 0', fontSize: '0.8em', gridColumn: '1 / -1' }}>
        Copyright ⓒ 2025 JUNWOO TECH Association. All Rights Reserved.(Version-0.1.3)
      </div>
    </div>
  );
}

export default HomePage;